import { useState } from 'react';
import {
  MoreHorizontal, Copy, Download, Heart, Pencil, Trash2, Ban, Flag,
  Pin, TrendingUp, Share2, User, Archive, Eraser, Hash, EyeOff, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './dropdown-menu';
import { useUserData } from '../UserDataContext';
import { useYapInteractions } from '../YapInteractionsContext';
import { useAuthGate } from '../../contexts/AuthGateContext';
import { copyToClipboard } from '../utils/clipboard';
import { YapService } from '../../services/api';
import {
  buildPostMenuOptions, isOwner, isShoutPost, pinTargetId,
  type PostMenuAction, type PostMenuGroup, type PostMenuPost, type PostSurface,
} from '../../utils/postMenuOptions';

/**
 * The ⋯ menu, everywhere.
 *
 * Every screen used to build this inline against the raw dropdown primitive —
 * eight copies — so the same post offered different options depending on where
 * it was viewed, and each new option had to be remembered in eight places.
 *
 * The option list is computed here from the post and the viewer, never passed
 * in, which is the whole point: a screen cannot drop an option by forgetting a
 * prop. The callbacks below are OUTCOME hooks, not gates. Omitting one does not
 * hide its option; the action still appears and falls back to a sensible
 * default, so a screen that has nothing special to do can pass nothing at all.
 *
 * The single exception is `group`, which Download needs and which lives on the
 * tribe/forum document rather than the post. Absent, Download is denied — a
 * privacy gate that fails open is worse than a missing entry.
 */

interface PostMenuPropsBase {
  post: PostMenuPost & { originalYapId?: string | null; content?: string };
  /** Which collection the post lives in, for the actions that need it. */
  collectionName?: string;
  /** Viewer owns the parent post — grants Delete, not Edit. See the module. */
  viewerOwnsParent?: boolean;

  /** Open the post's author. Defaults to a navigation event. */
  onViewProfile?: () => void;
  /** Open the share sheet. Defaults to copying the link. */
  onShare?: () => void;
  /** Open the edit modal. No default: only the screen knows where it renders. */
  onEditRequested?: () => void;
  /** Confirm-and-delete. Defaults to deleting after a confirm. */
  onDeleteRequested?: () => void;
  /** Called once a delete has actually happened, so the list can drop the row. */
  onDeleted?: () => void;
  /** Show the likers sheet. */
  onSeeLikes?: () => void;
  /** Open the report flow. Defaults to a navigation event. */
  onReport?: () => void;
  /** Profile shelf only. */
  onArchive?: () => void;
  /** Shout detail only. */
  onRemoveShoutExtras?: () => void;
  /** Legion soft-delete with an audit reason. */
  onLegionDelete?: () => void;
  /** Anything that changed the post, so the surface can refresh. */
  onChanged?: () => void;

  className?: string;
}

/**
 * Props, discriminated on `surface`.
 *
 * A tribe or forum call site passes surface="group" and therefore CANNOT
 * compile without `group`. That is deliberate: Download's privacy gate reads
 * it, and the runtime fails closed without it, so a forgotten prop would
 * silently remove an option rather than error. The type makes it a build
 * failure instead.
 */
export type PostMenuProps =
  | (PostMenuPropsBase & { surface: 'group'; group: PostMenuGroup })
  | (PostMenuPropsBase & {
      surface: Exclude<PostSurface, 'group'>;
      group?: PostMenuGroup | null;
    });

const LABELS: Record<PostMenuAction, string> = {
  'view-profile': 'View Profile',
  share: 'Share',
  'copy-link': 'Copy Link',
  download: 'Download',
  'see-likes': 'See Likes',
  edit: 'Edit',
  delete: 'Delete',
  report: 'Report',
  block: 'Block User',
  archive: 'Archive',
  'remove-shout-extras': 'Remove Caption & Images',
  'pin-feed': 'Pin to Timeline',
  'unpin-feed': 'Unpin from Timeline',
  'mark-trending': 'Mark as Trending',
  'unmark-trending': 'Unmark Trending',
  'hide-from-preview': 'Hide from Public Preview',
  'show-in-preview': 'Show in Public Preview',
  'copy-id': 'Copy Post ID',
  'legion-delete': 'Legion Delete',
};

const ICONS: Partial<Record<PostMenuAction, any>> = {
  'view-profile': User,
  share: Share2,
  'copy-link': Copy,
  download: Download,
  'see-likes': Heart,
  edit: Pencil,
  delete: Trash2,
  report: Flag,
  block: Ban,
  archive: Archive,
  'remove-shout-extras': Eraser,
  'pin-feed': Pin,
  'unpin-feed': Pin,
  'mark-trending': TrendingUp,
  'unmark-trending': TrendingUp,
  'hide-from-preview': EyeOff,
  'show-in-preview': Eye,
  'copy-id': Hash,
  'legion-delete': Trash2,
};

/** Destructive actions read red wherever they appear. */
const DESTRUCTIVE = new Set<PostMenuAction>(['delete', 'block', 'legion-delete', 'remove-shout-extras']);

export function PostMenu(props: PostMenuProps) {
  const { post, surface, group, collectionName, className } = props;
  const { userProfile, blockUser } = useUserData();
  const { getLikeCount, getShoutCount, getPostReplyCount } = useYapInteractions();
  const { isAuthed, requireAuth } = useAuthGate();
  const [busy, setBusy] = useState(false);

  const viewer = {
    id: userProfile?.id,
    rank: userProfile?.rank,
    isAuthed,
  };

  const editWindowMs = YapService.editWindowRemaining((post as any).timestamp);
  // Cast: destructuring widens `surface` back to PostSurface and loses the
  // discrimination. The narrowing that matters is enforced on PostMenuProps
  // above, at the call sites.
  type BuildInput = Parameters<typeof buildPostMenuOptions>[0];
  const options = buildPostMenuOptions({
    post, viewer, surface, group, editWindowMs,
    viewerOwnsParent: props.viewerOwnsParent,
  } as BuildInput);

  const postId = String(post.id);
  const authorId = String(
    isShoutPost(post) ? (post.shoutedBy ?? '') : (post.userId ?? post.authorId ?? ''),
  );

  const run = async (label: string, fn: () => Promise<void> | void) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } catch (e: any) {
      console.error(`[PostMenu] ${label} failed:`, e);
      toast.error(e?.message || `Could not ${label.toLowerCase()}`);
    } finally {
      setBusy(false);
    }
  };

  const navigate = (detail: string) =>
    window.dispatchEvent(new CustomEvent('navigateToScreen', { detail }));

  const handle = (action: PostMenuAction) => async () => {
    switch (action) {
      case 'view-profile':
        return props.onViewProfile ? props.onViewProfile() : navigate('profile');

      case 'share':
        if (props.onShare) return props.onShare();
        return run('Share', async () => {
          const ok = await copyToClipboard(`https://afroyap.com/yap/${postId}`);
          toast[ok ? 'success' : 'error'](ok ? 'Link copied' : 'Could not copy link');
        });

      case 'copy-link':
        return run('Copy link', async () => {
          const ok = await copyToClipboard(`https://afroyap.com/yap/${postId}`);
          toast[ok ? 'success' : 'error'](ok ? 'Link copied' : 'Could not copy link');
        });

      case 'download':
        return run('Download', async () => {
          const { downloadYapAsImage } = await import('../../utils/downloadYap');
          await downloadYapAsImage(post as any, { userProfile, getLikeCount, getShoutCount, getPostReplyCount });
        });

      case 'see-likes':
        return props.onSeeLikes?.();

      case 'edit':
        return props.onEditRequested?.();

      case 'delete':
        if (props.onDeleteRequested) return props.onDeleteRequested();
        return run('Delete', async () => {
          if (!window.confirm('Delete this post? This cannot be undone.')) return;
          await YapService.deleteYap(postId);
          toast.success('Deleted');
          props.onDeleted?.();
        });

      case 'report':
        return props.onReport ? props.onReport() : navigate('report');

      case 'block':
        return run('Block', async () => {
          if (!authorId) return;
          if (!window.confirm('Block this user? You will stop seeing their posts.')) return;
          await blockUser(authorId);
          toast.success('User blocked');
          props.onChanged?.();
        });

      case 'archive':
        return props.onArchive?.();

      case 'remove-shout-extras':
        return props.onRemoveShoutExtras?.();

      // Pinning follows the ORIGINAL yap for a shout: pinnedToFeed is read off
      // the yaps collection, so pinning the shout document would write a flag
      // nothing reads.
      case 'pin-feed':
        return run('Pin', async () => {
          await YapService.pinYapToFeed(
            pinTargetId(post),
            String(userProfile?.id || ''),
            String(userProfile?.username || ''),
          );
          toast.success('Pinned to timeline');
          props.onChanged?.();
        });

      case 'unpin-feed':
        return run('Unpin', async () => {
          await YapService.unpinYapFromFeed(pinTargetId(post));
          toast.success('Unpinned');
          props.onChanged?.();
        });

      case 'mark-trending':
        return run('Mark trending', async () => {
          await YapService.markAsTrending(postId, String(userProfile?.id || ''));
          toast.success('Marked as trending');
          props.onChanged?.();
        });

      case 'unmark-trending':
        return run('Unmark trending', async () => {
          await YapService.unmarkTrending(postId);
          toast.success('Trending removed');
          props.onChanged?.();
        });

      case 'hide-from-preview':
      case 'show-in-preview': {
        const hidden = action === 'hide-from-preview';
        return run(hidden ? 'Hide from preview' : 'Show in preview', async () => {
          await YapService.setHiddenFromPreview(postId, hidden, String(userProfile?.id || ''));
          toast.success(hidden ? 'Hidden from the logged-out preview' : 'Back in the logged-out preview');
          props.onChanged?.();
        });
      }

      case 'copy-id':
        return run('Copy ID', async () => {
          const ok = await copyToClipboard(postId);
          toast[ok ? 'success' : 'error'](ok ? 'Post ID copied' : 'Could not copy');
        });

      case 'legion-delete':
        return props.onLegionDelete?.();
    }
  };

  const trigger = (
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        onClick={e => e.stopPropagation()}
        aria-label="Post options"
        className={
          className ||
          'text-[#666666] dark:text-[#E5E5E5] hover:text-[#4478ea] transition-colors rounded-full p-1'
        }
      >
        <MoreHorizontal className="w-5 h-5" />
      </button>
    </DropdownMenuTrigger>
  );

  const content = 'p-1 bg-[#FAF8F5] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 modal-shadow';
  const itemBase = 'cursor-pointer rounded-lg focus:bg-[#4478ea]/10';

  // A signed-out visitor is offered the way in, not a list of actions that
  // would each fail on their own.
  if (!isAuthed) {
    return (
      <DropdownMenu>
        {trigger}
        <DropdownMenuContent className={content} onClick={e => e.stopPropagation()}>
          <DropdownMenuItem
            className={`text-black dark:text-white ${itemBase}`}
            onClick={() => requireAuth(undefined, 'Create an account to do more')}
          >
            <User className="w-4 h-4 mr-2" />
            Log in or sign up
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (options.length === 0) return null;

  return (
    <DropdownMenu>
      {trigger}
      <DropdownMenuContent className={content} onClick={e => e.stopPropagation()}>
        {options.map(action => {
          const Icon = ICONS[action];
          return (
            <DropdownMenuItem
              key={action}
              disabled={busy}
              className={`${DESTRUCTIVE.has(action) ? 'text-[#E74C3C]' : 'text-black dark:text-white'} ${itemBase}`}
              onClick={handle(action)}
            >
              {Icon && <Icon className="w-4 h-4 mr-2" />}
              {LABELS[action]}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { isOwner };
export type { PostMenuPost, PostSurface, PostMenuGroup };
